/**
 * 云端计费 V2 业务封装：接口调用、余额全局同步、展示工具。
 */
import {
  createCloudPipePayQr as apiCreateCloudPipePayQr,
  createStoragePayQr as apiCreateStoragePayQr,
  createWalletRechargeQr as apiCreateWalletRechargeQr,
  getCloudBillingOverview as apiGetOverview,
  getCloudMyPackage as apiGetMyPackage,
  getCloudWalletLedger as apiGetLedger,
  getCloudWalletSummary as apiGetWalletSummary,
  getPayStatus,
  listCloudBillingOrders as apiListOrders,
  listCloudDailyBills as apiListDailyBills,
  listCloudQuotaPackages as apiListPackages,
  listCloudStorageProducts as apiListStorage,
  listCloudWalletRecharges as apiListRecharges,
  payCloudPipeByBalance as apiPayCloudPipeBalance,
  payStorageByBalance as apiPayStorageBalance,
  previewAlgorithmPushFee as apiPreviewPushFee,
  quoteCloudBilling as apiQuote,
  quoteCloudStorage as apiQuoteStorage,
  getMyAlgorithmList,
  queryBoundCloudDevices,
  switchCloudExcessMode as apiSwitchExcessMode,
} from '@/services/ant-design-pro/api';

export type BillingCycle = API.BillingCycle;
export type StorageCycle = API.StorageCycle;
export type ExcessMode = API.ExcessMode;
export type PayChannel = 'alipay' | 'wx';

export const CYCLE_LABEL: Record<string, string> = {
  MONTH: '月付',
  M: '月付',
  QUARTER: '季付',
  Q: '季付',
  YEAR: '年付',
  Y: '年付',
  HALF_YEAR: '半年付',
  H: '半年付',
};

/** 计费周期展示（兼容 MONTH / M 等） */
export function formatBillingCycle(cycle?: string | null): string {
  if (!cycle) return '—';
  return CYCLE_LABEL[cycle] || CYCLE_LABEL[cycle.toUpperCase()] || cycle;
}

/** 接口时间展示（去掉 T，截到分钟） */
export function formatDateTime(t?: string | null): string {
  if (!t) return '—';
  return String(t)
    .replace('T', ' ')
    .replace(/\.\d+.*$/, '')
    .slice(0, 16);
}

const CYCLE_TOKENS: Record<string, string[]> = {
  M: ['M', 'MONTH', '月付', '月'],
  MONTH: ['M', 'MONTH', '月付', '月'],
  Q: ['Q', 'QUARTER', '季付', '季'],
  QUARTER: ['Q', 'QUARTER', '季付', '季'],
  Y: ['Y', 'YEAR', '年付', '年'],
  YEAR: ['Y', 'YEAR', '年付', '年'],
  H: ['H', 'HALF_YEAR', '半年付', '半年'],
  HALF_YEAR: ['H', 'HALF_YEAR', '半年付', '半年'],
};

/** 套餐名已含周期信息时不再重复拼接 */
export function packageNameHasCycle(
  packageName: string,
  billingCycle?: string | null,
): boolean {
  if (!billingCycle) return false;
  const tokens =
    CYCLE_TOKENS[billingCycle] ||
    CYCLE_TOKENS[billingCycle.toUpperCase()] ||
    [billingCycle];
  const hay = packageName.toUpperCase();
  return tokens.some((t) => hay.includes(String(t).toUpperCase()));
}

/** 工作台 / 套餐摘要一行文案 */
export function formatPackageBriefLine(
  brief?: API.CloudBillingPackageBriefVO | null,
  connectedFallback = 0,
): string | null {
  if (!brief?.packageName && !brief?.expireTime) return null;
  const name = brief.packageName || '—';
  const cycle = brief.billingCycle
    ? formatBillingCycle(brief.billingCycle)
    : '';
  const showCycle =
    !!cycle &&
    cycle !== '—' &&
    !packageNameHasCycle(name, brief.billingCycle);
  const connected = brief.connectedChannels ?? connectedFallback;
  const limit = brief.channelLimit ?? 0;
  const expirePart = `${formatDateTime(brief.expireTime)} 到期${
    brief.remainDays != null ? `（剩 ${brief.remainDays} 天）` : ''
  }`;
  const parts = [
    name,
    showCycle ? cycle : null,
    expirePart,
    `已接入 ${connected}/${limit} 路`,
  ].filter(Boolean);
  return parts.join(' · ');
}

/** 存储摘要主文案 */
export function formatStorageBriefTitle(
  store?: API.CloudBillingStorageBriefVO | null,
): string {
  if (!store) return '暂未订阅';
  if (store.desc) return store.desc;
  if (store.storageType) {
    return `${store.storageType} · ${store.roads ?? '—'} 路`;
  }
  if ((store.itemCount ?? 0) > 0) return `${store.itemCount} 项订阅`;
  if (store.expireTime) return '已订阅';
  return '暂未订阅';
}

export function hasStorageBrief(
  store?: API.CloudBillingStorageBriefVO | null,
): boolean {
  return !!(
    store &&
    (store.desc ||
      store.storageType ||
      store.expireTime ||
      (store.itemCount ?? 0) > 0)
  );
}

export const EXCESS_MODE_LABEL: Record<ExcessMode, string> = {
  BANDWIDTH: '按带宽',
  TRAFFIC: '按流量',
};

export function fmtMoney(n: number | undefined | null): string {
  const v = Number(n) || 0;
  return '¥' + v.toFixed(2);
}

export function cycleToApi(mult: 1 | 3 | 12): BillingCycle {
  if (mult === 3) return 'QUARTER';
  if (mult === 12) return 'YEAR';
  return 'MONTH';
}

export function storageCycleFromIndex(
  product: API.StorageProductVO,
  idx: number,
): StorageCycle {
  const cycles = getStorageCycles(product);
  return cycles[idx]?.key ?? 'YEAR';
}

export function getStorageCycles(product: API.StorageProductVO): {
  key: StorageCycle;
  label: string;
  price: number;
}[] {
  const list: { key: StorageCycle; label: string; price: number }[] = [];
  if (product.monthPrice != null && product.monthPrice > 0) {
    list.push({
      key: 'MONTH',
      label: '月付',
      price: Number(product.monthPrice),
    });
  }
  if (product.halfYearPrice != null && product.halfYearPrice > 0) {
    list.push({
      key: 'HALF_YEAR',
      label: '半年付',
      price: Number(product.halfYearPrice),
    });
  }
  if (product.yearPrice != null && product.yearPrice > 0) {
    list.push({
      key: 'YEAR',
      label: '年付',
      price: Number(product.yearPrice),
    });
  }
  if (list.length === 0 && product.yearPrice != null) {
    list.push({
      key: 'YEAR',
      label: '年付',
      price: Number(product.yearPrice) || 0,
    });
  }
  return list;
}

export function matchDiscount(
  discounts: API.StorageDiscountVO[] | undefined,
  roads: number,
): number {
  if (!discounts?.length) return 1;
  const sorted = [...discounts].sort(
    (a, b) => b.roadThreshold - a.roadThreshold,
  );
  for (const d of sorted) {
    if (roads >= d.roadThreshold) return Number(d.discountRate) || 1;
  }
  return 1;
}

function unwrap<T>(res: API.ApiResponse<T>, fallbackMsg: string): T {
  if (res.code === 200 && res.data !== undefined && res.data !== null) {
    return res.data;
  }
  throw new Error(res.message || fallbackMsg);
}

export async function fetchBillingOverview() {
  return unwrap(await apiGetOverview(), '获取计费概览失败');
}

/** 已推送算法的本月预估（与「我的算法」列表 monthEstimateFee 汇总一致） */
export async function fetchPushedAlgoMonthEstimate(): Promise<{
  amount: number;
  remark?: string;
}> {
  let listSum = 0;
  let pushedAlgoCount = 0;
  let pushedRoads = 0;
  try {
    const res = await getMyAlgorithmList();
    const algos = res.code === 200 && res.data ? res.data : [];
    algos.forEach((a) => {
      const ids = a.pushedGroupIds ?? [];
      const names = a.pushedGroupNames ?? [];
      const pushed = ids.length > 0 || names.length > 0;
      if (!pushed) return;
      pushedAlgoCount += 1;
      pushedRoads += Number(a.pushedRoadCount ?? 0);
      listSum += Number(a.monthEstimateFee ?? 0);
    });
  } catch {
    /* 列表失败时走 my-package / overview */
  }

  try {
    const pkg = await fetchMyPackage();
    const pkgEst = pkg?.algo?.monthEstimate;
    const amount =
      listSum > 0
        ? listSum
        : pkgEst != null
          ? Number(pkgEst)
          : 0;
    const remark =
      pkg?.algo?.remark ||
      (pushedAlgoCount > 0
        ? `已推送 ${pushedAlgoCount} 个算法 · ${pushedRoads} 路`
        : undefined);
    return { amount, remark };
  } catch {
    return {
      amount: listSum,
      remark:
        pushedAlgoCount > 0
          ? `已推送 ${pushedAlgoCount} 个算法 · ${pushedRoads} 路`
          : undefined,
    };
  }
}

export async function fetchMyPackage() {
  return unwrap(await apiGetMyPackage(), '获取云端套餐失败');
}

export async function switchExcessMode(mode: ExcessMode) {
  const res = await apiSwitchExcessMode({ mode });
  if (res.code !== 200) throw new Error(res.message || '切换超额模式失败');
}

export async function fetchQuotaPackages() {
  const list = unwrap(await apiListPackages(), '获取管道套餐失败');
  return (list || []).filter((p) => p.status == null || p.status === 1);
}

export async function fetchStorageProducts() {
  return unwrap(await apiListStorage(), '获取存储商品失败');
}

export async function quoteBilling(body: API.CloudBillingQuoteDTO) {
  return unwrap(await apiQuote(body), '费用试算失败');
}

export async function createPipePayQr(body: API.CloudPipePayCreateDTO) {
  return unwrap(await apiCreateCloudPipePayQr(body), '生成支付二维码失败');
}

export async function payPipeByBalance(body: API.CloudPipePayCreateDTO) {
  const res = await apiPayCloudPipeBalance(body);
  if (res.code !== 200) throw new Error(res.message || '余额支付失败');
  return res.data;
}

export async function quoteStorage(body: API.StorageQuoteDTO) {
  return unwrap(await apiQuoteStorage(body), '存储试算失败');
}

export async function createStorageQr(body: API.StoragePayCreateDTO) {
  return unwrap(await apiCreateStoragePayQr(body), '生成存储支付二维码失败');
}

export async function payStorageBalance(body: API.StoragePayCreateDTO) {
  const res = await apiPayStorageBalance(body);
  if (res.code !== 200) throw new Error(res.message || '余额支付失败');
  return res.data;
}

export async function previewPushFee(body: API.PushFeePreviewDTO) {
  return unwrap(await apiPreviewPushFee(body), '费用预估失败');
}

export async function fetchWalletSummary() {
  return unwrap(await apiGetWalletSummary(), '获取钱包摘要失败');
}

export async function createRechargeQr(body: API.WalletRechargeQrDTO) {
  return unwrap(await apiCreateWalletRechargeQr(body), '生成充值二维码失败');
}

export async function fetchWalletLedger(current = 1, size = 20) {
  return (
    unwrap(await apiGetLedger({ current, size }), '获取扣款流水失败') || []
  );
}

export async function fetchDailyBills(month: string) {
  return unwrap(await apiListDailyBills({ month }), '获取日结算账单失败') || [];
}

export async function fetchBillingOrders(current = 1, size = 20) {
  return (
    unwrap(await apiListOrders({ current, size }), '获取订单记录失败') || []
  );
}

export async function fetchWalletRecharges(current = 1, size = 20) {
  const data = unwrap(
    await apiListRecharges({ current, size }),
    '获取充值记录失败',
  );
  return data?.records ?? [];
}

export async function queryPayStatus(orderNo: string) {
  const res = await getPayStatus({ orderNo }, { skipErrorHandler: true });
  return unwrap(res, '查询支付状态失败');
}

export type DeviceGroupRow = {
  name: string;
  roads: number;
  algos: number;
  status: string;
};

/** 按分组聚合云端绑定摄像头 */
export async function fetchBoundDeviceGroups(): Promise<{
  groups: DeviceGroupRow[];
  total: number;
}> {
  const res = await queryBoundCloudDevices();
  const list = unwrap(res, '\u83b7\u53d6\u7ed1\u5b9a\u8bbe\u5907\u5931\u8d25') || [];
  const algoByGroup = new Map<string, number>();
  try {
    const algoRes = await getMyAlgorithmList();
    const algos = algoRes.code === 200 && algoRes.data ? algoRes.data : [];
    algos.forEach((algo) => {
      (algo.pushedGroupNames || []).forEach((name) => {
        if (!name) return;
        algoByGroup.set(name, (algoByGroup.get(name) || 0) + 1);
      });
    });
  } catch {
    /* \u5728\u5f00\u7b97\u6cd5\u6570\u7f3a\u5931\u65f6\u4ecd\u5c55\u793a\u8def\u6570 */
  }
  const map = new Map<string, DeviceGroupRow>();
  list.forEach((d) => {
    const name = d.groupName || d.name || '未分组';
    const cur =
      map.get(name) || { name, roads: 0, algos: 0, status: '正常' };
    cur.roads += 1;
    cur.algos = algoByGroup.get(name) || cur.algos;
    if (d.bindStatus === '1' || d.status === 'EXPIRED') {
      cur.status = '已过期';
    }
    map.set(name, cur);
  });
  algoByGroup.forEach((count, name) => {
    const cur = map.get(name);
    if (cur) cur.algos = count;
  });
  return { groups: [...map.values()], total: list.length };
}

// ----- 顶栏余额全局同步 -----

type WalletListener = (w: API.WalletSummaryVO | null) => void;
let walletCache: API.WalletSummaryVO | null = null;
const walletListeners = new Set<WalletListener>();
let walletLoading: Promise<API.WalletSummaryVO | null> | null = null;

export function getCachedWallet(): API.WalletSummaryVO | null {
  return walletCache;
}

export function subscribeWallet(listener: WalletListener): () => void {
  walletListeners.add(listener);
  listener(walletCache);
  return () => walletListeners.delete(listener);
}

function notifyWallet() {
  walletListeners.forEach((fn) => fn(walletCache));
}

export async function refreshWallet(force = false): Promise<API.WalletSummaryVO | null> {
  if (!force && walletLoading) return walletLoading;
  walletLoading = (async () => {
    try {
      walletCache = await fetchWalletSummary();
      notifyWallet();
      return walletCache;
    } catch {
      return walletCache;
    } finally {
      walletLoading = null;
    }
  })();
  return walletLoading;
}
